import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Modal, Field, Notice } from '../patterns';

interface ValidPest { code: string; name: string; }

interface Props {
  show: boolean;
  tenantId: string;
  orphanCode: string;
  onHide: () => void;
  onRemapped: (result: { records: number; configs: number; from: string; to: string }) => void;
}

const RemapPestModal: React.FC<Props> = ({ show, tenantId, orphanCode, onHide, onRemapped }) => {
  const { t } = useTranslation();
  const [pests, setPests] = useState<ValidPest[]>([]);
  const [target, setTarget] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show || !tenantId) return;
    setError(null);
    setTarget('');
    // Fetch valid remap targets: local-tenant-pests + global-pests, deduplicated by code.
    Promise.all([
      axios.get(`/api/v1/global-pests/tenant-pests/${encodeURIComponent(tenantId)}`, { withCredentials: true }),
      axios.get('/api/v1/global-pests', { withCredentials: true }),
    ]).then(([localRes, globalRes]) => {
      const seen = new Set<string>();
      const combined: ValidPest[] = [];
      for (const p of [...(localRes.data as any[]), ...(globalRes.data as any[])]) {
        if (!seen.has(p.code)) {
          seen.add(p.code);
          combined.push({ code: p.code, name: p.name });
        }
      }
      setPests(combined.sort((a, b) => a.code.localeCompare(b.code)));
    }).catch(() => {
      setError(t('system.integrity.errors.loadValidPestsFailed'));
    });
  }, [show, tenantId, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post('/api/v1/integrity/remap', {
        tenantId,
        oldCode: orphanCode,
        newCode: target,
      }, { withCredentials: true });
      onRemapped({
        records: res.data.scoutingRecordsUpdated || 0,
        configs: res.data.configsUpdated || 0,
        from: orphanCode,
        to: target,
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || t('system.integrity.errors.remapFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} title={t('system.integrity.remap.title')} size="md">
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <p>{t('system.integrity.remap.body', { code: orphanCode })}</p>
          <Field label={t('system.integrity.remap.targetLabel')} controlId="remap-target" required>
            <select
              id="remap-target"
              className="pat-input"
              value={target}
              onChange={e => setTarget(e.target.value)}
              required
            >
              <option value="">{t('system.integrity.remap.targetPlaceholder')}</option>
              {pests.map(p => (
                <option key={p.code} value={p.code}>{p.name} ({p.code})</option>
              ))}
            </select>
          </Field>
          {error && <Notice tone="danger">{error}</Notice>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="pat-btn pat-btn--subtle" onClick={onHide} disabled={submitting}>
            {t('system.integrity.remap.cancel')}
          </button>
          <button type="submit" className="pat-btn pat-btn--primary" disabled={submitting || !target}>
            {t('system.integrity.remap.submit')}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default RemapPestModal;
